import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import "./real-stories.css";
import Loading from "../../loading/Loading";
import LoadingDashboard from "../../loading/LoadingDashboard";

const RealStories = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const TESTIMONIALS_API_ENDPOINT = "/wp-json/dbsb/v1/testimonials";

  // Fetch testimonials data
  useEffect(() => {
    const fetchTestimonials = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${TESTIMONIALS_API_ENDPOINT}?per_page=50&page=1`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch testimonials.");
        }

        const data = await response.json();
        const mappedTestimonials = data.testimonials.map((testimonial) => ({
          quote: testimonial.quote || "",
          name: testimonial.author_name || testimonial.title || "Anonymous",
          role: testimonial.author_role || "Patient",
          featured_image: testimonial.featured_image,
          id: testimonial.id,
          date: testimonial.date,
        }));

        setTestimonials(mappedTestimonials);
      } catch (error) {
        console.error("Error fetching testimonials:", error);
        setError(error.message);
        // Fallback to sample data if API fails
        setTestimonials([
          {
            quote: "As a healthcare provider, I love the streamlined test ordering and management features. It saves me and my patients so much time!",
            name: "Dr. Anthony K.",
            role: "Healthcare provider",
          },
          {
            quote: "The process was incredibly simple and the results were easy to understand. The genetic counselor was very helpful in explaining everything.",
            name: "Jane D.",
            role: "Patient",
          },
          {
            quote: "Direct by Syndicate Bio has revolutionized how we approach genetic testing in our clinic. The platform is robust and user-friendly.",
            name: "Dr. Fatima S.",
            role: "Clinical Geneticist",
          },
          {
            quote: "I was hesitant at first, but the level of support and privacy made me feel secure. Getting insights into my health was empowering.",
            name: "Michael B.",
            role: "Patient",
          },
          {
            quote: "The turnaround time for results is impressive. This allows us to make timely clinical decisions for our patients.",
            name: "Dr. Chioma N.",
            role: "Oncologist",
          },
          {
            quote: "A fantastic service that makes complex genetic information accessible and affordable for everyone. Highly recommended.",
            name: "Sarah L.",
            role: "Healthcare Advocate",
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  const itemsPerPage = isMobile ? 1 : 3;
  
  // The total number of unique slider positions (e.g., for 6 items and 3 per page, there are 4 positions: 1-3, 2-4, 3-5, 4-6)
  const totalSlides = testimonials.length > itemsPerPage ? testimonials.length - itemsPerPage + 1 : 1;
  const maxIndex = totalSlides - 1;

  // Effect for handling window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Effect for the automatic slider
  useEffect(() => {
    if (maxIndex > 0) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex >= maxIndex ? 0 : prevIndex + 1));
      }, 5000); // Slide every 5 seconds
      return () => clearInterval(interval);
    }
  }, [maxIndex]);

  // Calculate which progress bar segment should be active based on the user's formula
  const activeBar = Math.ceil(((currentIndex + 1) / totalSlides) * 3);
  
  const sectionVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } },
  };

  if (isLoading) {
    return (
      <motion.section 
        className="real-stories-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="real-stories-container">
          <div className="real-stories-title-container">
            <motion.h2 className="real-stories-title">
              Real Stories, Real Results
            </motion.h2>
          </div>
          <div className="loading-container">
            <LoadingDashboard />
          </div>
        </div>
      </motion.section>
    );
  }

  if (testimonials.length === 0) {
    return (
      <motion.section 
        className="real-stories-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="real-stories-container">
          <div className="real-stories-title-container">
            <motion.h2 className="real-stories-title">
              Real Stories, Real Results
            </motion.h2>
          </div>
          <div className="no-testimonials">
            <p>No testimonials available at the moment.</p>
          </div>
        </div>
      </motion.section>
    );
  }

  return (
    <motion.section 
      className="real-stories-section"
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
    >
      <div className="real-stories-container">
        <div className="real-stories-title-container">
        <motion.h2 className="real-stories-title" variants={itemVariants}>
            Real Stories, Real Results
        </motion.h2>
        </div>

        <div className="slider-container">
          <img
            src={`${process.env.PUBLIC_URL}/assets/quote.svg`}
            alt="Quote icon"
            className="quote-svg"
          />
        <div className="slider-viewport">
          <motion.div
            className="slider-track"
            animate={{ x: `-${currentIndex * (100 / itemsPerPage)}%` }}
            transition={{ type: "tween", ease: "easeInOut", duration: 0.8 }}
          >
            {testimonials.map((testimonial, index) => (
              <div
                className="testimonial-card-wrapper"
                key={testimonial.id || index}
                style={{ flexBasis: `${100 / itemsPerPage}%` }}
              >
                <div className="testimonial-card">
                  <p className="testimonial-quote">"{testimonial.quote}"</p>
                  <hr className="testimonial-divider" />
                  <div className="testimonial-author">
                    <span className="author-name">{testimonial.name}</span>
                    <span className="author-role">{testimonial.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
        </div>

        <div className="progress-bar-container">
          <div className={`progress-line ${activeBar === 1 ? "active" : ""}`} />
          <div className={`progress-line ${activeBar === 2 ? "active" : ""}`} />
          <div className={`progress-line ${activeBar === 3 ? "active" : ""}`} />
        </div>
      </div>
    </motion.section>
  );
};

export default RealStories;